// Names whose pronunciation does not follow from English spelling rules, spelled as they sound.
export const IRREGULAR_PRONUNCIATIONS: ReadonlyMap<string, string> = new Map([
  ["sean", "shawn"],
  ["geoffrey", "jeffrey"],
  ["mohammed", "muhamad"],
  ["mohammad", "muhamad"],
  ["mohamed", "muhamad"],
  ["muhammad", "muhamad"],
  ["muhammed", "muhamad"],
  ["jesse", "jessie"],
  ["renee", "rene"],
  ["shepherd", "sheppard"],
  ["micheal", "michael"],
  ["key", "kee"],
]);

// Spellings of one name that the pronunciation rules cannot equate without also equating different
// names (making -ine sound like -in for Jasmine/Jasmin would also match Christine/Kristin). Add a
// group only when the spellings are commonly pronounced the same.
const KNOWN_HOMOPHONE_GROUPS: readonly (readonly string[])[] = [
  ["jasmine", "jasmin", "jazmin", "jazmine"],
  ["tanya", "tonya"],
  ["cohen", "cohn"],
  ["isabel", "isabelle"],
  ["jeffery", "jeffrey", "geoffrey"],
  ["leila", "layla", "laila"],
  // Romanization variants of one Korean or Chinese surname.
  ["choi", "choe", "choy"],
  ["yoon", "yun"],
  ["yoo", "yu"],
  ["moon", "mun"],
  ["wang", "wong"],
  ["chou", "chow"],
  // Spellings whose syllable count or vowels differ from the common form but are said the same.
  ["brittany", "britney", "brittney", "britany", "brittani", "britni"],
  ["caitlin", "kaitlyn", "katelyn", "caitlyn", "kaitlin", "katlyn", "kaitlynn"],
  ["katherine", "catherine", "kathryn", "kathrine", "katharine", "catharine"],
  ["deborah", "debra", "debora", "debrah"],
  ["isaac", "isaak", "issac", "izaak"],
  ["skyler", "skylar", "schuyler"],
  ["jamie", "jaime", "jaimie", "jamey"],
  ["damian", "damien", "damion"],
  ["michelle", "michele"],
  ["chelsea", "chelsey", "chelsie"],
  ["elizabeth", "elisabeth"],
  ["ximena", "jimena"],
  ["deepak", "dipak"],
  ["lakshmi", "laxmi"],
  ["sanjay", "sanjai"],
  ["meyer", "myer", "meier", "maier", "mayer"],
  ["siddiqui", "siddiqi"],
  ["martha", "marta"],
  ["mackenzie", "mckenzie", "makenzie"],
  ["frances", "francis"],
];
const KNOWN_HOMOPHONE_GROUP_BY_NAME = new Map(
  KNOWN_HOMOPHONE_GROUPS.flatMap((group, index) => group.map((name) => [name, index] as const))
);

// Common names that share a pronunciation key but are said differently, usually because spelling
// does not show stress or vowel length (Casey is long, Cassie short; Marie is ma-REE, Mary MAIR-ee).
// Every name in one list is distinct from every name in the other.
const KNOWN_DISTINCT_GROUPS: readonly (readonly [readonly string[], readonly string[]])[] = [
  [["marie"], ["mary", "mari"]],
  [
    ["casey", "kasey", "kacey"],
    ["cassie", "cassy", "kassie"],
  ],
  [["haley", "hailey", "hayley"], ["hallie"]],
  [
    ["wiley", "wylie"],
    ["willie", "willey"],
  ],
  [["janie"], ["jannie"]],
  [["dona"], ["donna"]],
  [["ivan"], ["yvonne"]],
  [["joanna"], ["jonah"]],
  [["eli"], ["ellie"]],
  [["luis"], ["louis", "lewis", "louise"]],
  [
    ["deanna", "deana"],
    ["diana", "dianna"],
  ],
  // Both stress the final syllable (mu-ÑIZ, mu-ÑOZ); the first-vowel stress rule reduces the vowel that differs.
  [["muniz"], ["munoz"]],
];
const KNOWN_DISTINCT = new Set(
  KNOWN_DISTINCT_GROUPS.flatMap(([left, right]) =>
    left.flatMap((a) => right.flatMap((b) => [`${a}/${b}`, `${b}/${a}`]))
  )
);

/** An explicit exception wins over pronunciation rules; null means there is no exception. */
export function knownNamesSoundAlike(left: string, right: string): boolean | null {
  if (KNOWN_DISTINCT.has(`${left}/${right}`)) return false;

  const leftGroup = KNOWN_HOMOPHONE_GROUP_BY_NAME.get(left);

  if (leftGroup !== undefined && leftGroup === KNOWN_HOMOPHONE_GROUP_BY_NAME.get(right)) return true;

  return null;
}
